import React, { useEffect, useState } from 'react';
import { Landmark, Save, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { ClientBankingSettingsService } from '../../services/ClientBankingSettingsService';
import type { BankingInfo } from '../../types/banking';

interface BankingConfigModalProps {
  isOpen: boolean;
  clientId?: string;
  clientName?: string;
  initialValue: BankingInfo;
  onClose: () => void;
  onSaved: (bankingInfo: BankingInfo) => void;
}

const PIX_TYPE_OPTIONS = ['CNPJ', 'CPF', 'E-mail', 'Telefone', 'Chave Aleatória'];

export default function BankingConfigModal({
  isOpen,
  clientId,
  clientName,
  initialValue,
  onClose,
  onSaved,
}: BankingConfigModalProps) {
  const [formData, setFormData] = useState<BankingInfo>(initialValue);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData(initialValue);
    }
  }, [initialValue, isOpen]);

  if (!isOpen) {
    return null;
  }

  const updateField = (field: keyof BankingInfo, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!clientId) {
      toast.error('Tenant não selecionado.');
      return;
    }

    setIsSaving(true);
    try {
      const savedBankingInfo = await ClientBankingSettingsService.upsertForClient(clientId, formData);
      onSaved(savedBankingInfo);
      toast.success('Dados bancários atualizados com sucesso.');
      onClose();
    } catch (error: any) {
      console.error('Erro ao salvar dados bancários:', error);
      toast.error(error?.message ?? 'Não foi possível salvar os dados bancários.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-blue-100 p-2 text-blue-700">
              <Landmark className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Dados Bancários</h2>
              <p className="text-sm text-gray-500">
                {clientName ? `Configuração financeira de ${clientName}` : 'Configuração financeira da clínica'}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
            aria-label="Fechar modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 px-6 py-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="mb-2 block text-sm font-medium text-gray-700">Beneficiário</label>
              <input
                type="text"
                value={formData.beneficiary}
                onChange={(event) => updateField('beneficiary', event.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Banco</label>
              <input
                type="text"
                value={formData.bankName}
                onChange={(event) => updateField('bankName', event.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Tipo de Conta</label>
              <input
                type="text"
                value={formData.accountType || ''}
                onChange={(event) => updateField('accountType', event.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ex.: Conta Corrente"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Agência</label>
              <input
                type="text"
                value={formData.agency}
                onChange={(event) => updateField('agency', event.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Conta</label>
              <input
                type="text"
                value={formData.account}
                onChange={(event) => updateField('account', event.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Tipo de PIX</label>
              <select
                value={formData.pixType}
                onChange={(event) => updateField('pixType', event.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                {PIX_TYPE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Chave PIX</label>
              <input
                type="text"
                value={formData.pixKey}
                onChange={(event) => updateField('pixKey', event.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Save className="h-4 w-4" />
              {isSaving ? 'Salvando...' : 'Salvar Dados'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
